import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowLeft } from 'lucide-react';
import { navigation } from '../data/mock';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Close mobile menu on Esc
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header
      role="banner"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? 'bg-cream/95 backdrop-blur-md border-b border-navy/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="group flex items-center gap-2"
            aria-label="Gal&Blizovsky - לעמוד הבית"
          >
            <span className="font-serif text-xl lg:text-2xl tracking-tight text-navy">
              Gal<span className="text-bronze" aria-hidden="true">&</span>Blizovsky
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav
            className="hidden lg:flex items-center gap-8"
            aria-label="ניווט ראשי"
          >
            {navigation.slice(1).map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors duration-300 fancy-link ${
                    isActive ? 'text-navy' : 'text-navy/70 hover:text-navy'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden lg:block">
            <Link
              to="/contact"
              className="btn-cta inline-flex items-center gap-2 bg-navy text-cream px-5 py-3 text-sm tracking-wide hover:bg-navy-700 transition-colors duration-300"
            >
              שיחת אבחון
              <ArrowLeft className="arrow w-4 h-4" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            type="button"
            className="lg:hidden text-navy p-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'סגור תפריט' : 'פתח תפריט'}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <nav
        id="mobile-nav"
        aria-label="ניווט נייד"
        className={`mobile-menu ${open ? 'open' : ''} lg:hidden`}
        aria-hidden={!open}
      >
        <div className="flex flex-col gap-1 mt-6">
          {navigation.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              tabIndex={open ? 0 : -1}
              className={({ isActive }) =>
                `text-2xl font-serif py-3 border-b border-cream/10 transition-colors ${
                  isActive ? 'text-bronze' : 'text-cream/80 hover:text-bronze'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            tabIndex={open ? 0 : -1}
            className="mt-8 inline-flex items-center justify-between bg-bronze text-navy px-6 py-4 text-base font-medium"
          >
            קבעו שיחת אבחון
            <ArrowLeft className="w-5 h-5" aria-hidden="true" />
          </Link>
        </div>
      </nav>
    </header>
  );
};

export default Header;
